import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { useGetTransactionsQuery } from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';
import { formatDate } from '../../utils/dateFormatter';

export function AdminTransactionsPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') ?? '';

  const [page, setPage] = useState(1);
  const [selectedTx, setSelectedTx] = useState<any | null>(null); // حالة المعاملة المختارة
  const limit = 20;

  // Filters – تهيئة userId من الرابط إن وُجد (مثلاً من صفحة تفاصيل المستخدم)
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState<string>(userIdFromUrl);

  useEffect(() => {
    if (userIdFromUrl && userIdFilter !== userIdFromUrl) {
      setUserIdFilter(userIdFromUrl);
      setPage(1);
    }
  }, [userIdFromUrl]);
  const [currencyFilter, setCurrencyFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [minAmountFilter, setMinAmountFilter] = useState<string>('');
  const [maxAmountFilter, setMaxAmountFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt' | 'amount' | 'type' | 'status'>('createdAt');
  const [sortOrder, setSortOrder] = useState<-1 | 1>(-1);

  const { data, isLoading, error, refetch } = useGetTransactionsQuery({
    page,
    limit,
    type: typeFilter || undefined,
    status: statusFilter as 'pending' | 'completed' | 'failed' | 'reversed' | undefined,
    paymentMethod: paymentMethodFilter || undefined,
    userId: userIdFilter || undefined,
    currency: currencyFilter || undefined,
    dateFrom: dateFromFilter || undefined,
    dateTo: dateToFilter || undefined,
    minAmount: minAmountFilter ? parseFloat(minAmountFilter) : undefined,
    maxAmount: maxAmountFilter ? parseFloat(maxAmountFilter) : undefined,
    search: searchFilter || undefined,
    sortBy,
    sortOrder,
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setPaymentMethodFilter('');
    setUserIdFilter('');
    setCurrencyFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setSearchFilter('');
    setSortBy('createdAt');
    setSortOrder(-1);
    setPage(1);
  };

  const getTransactionTypeLabel = (type: string, paymentDetails?: Record<string, any>) => {
    if (type === 'purchase' && (paymentDetails?.type === 'renewal' || paymentDetails?.isRenewal === true)) {
      return t('admin.transactions.types.purchase_renewal', { defaultValue: 'شراء (تجديد)' });
    }
    return t(`admin.transactions.types.${type}`, { defaultValue: type });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-500';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-500';
      case 'failed':
        return 'bg-red-500/20 text-red-500';
      case 'reversed':
        return 'bg-orange-500/20 text-orange-500';
      default:
        return 'bg-neutral-dark/50 text-neutral-medium';
    }
  };

  const getAmountColor = (amount: number) => {
    if (amount > 0) return 'text-green-500';
    if (amount < 0) return 'text-red-500';
    return 'text-neutral-medium';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-danger mb-4">
            {t('admin.transactions.error.title')}
          </h2>
          <p className="text-neutral-medium font-body mb-6">
            {'data' in (error as { data?: { message?: string } })
              ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
              : t('errors.network')}
          </p>
          <button
            onClick={() => void refetch()}
            className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const transactions = data?.data.transactions || [];
  const pagination = data?.data.pagination;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.transactions.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.transactions.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="card-glass rounded-lg p-4 mb-6 border border-neutral-dark">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.type')}
            </label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.transactions.filters.all')}</option>
              <option value="recharge">{t('admin.transactions.types.recharge')}</option>
              <option value="purchase">{t('admin.transactions.types.purchase')}</option>
              <option value="renewal">{t('admin.transactions.types.purchase_renewal')}</option>
              <option value="referral_bonus">{t('admin.transactions.types.referral_bonus')}</option>
              <option value="refund">{t('admin.transactions.types.refund')}</option>
              <option value="admin_add">{t('admin.transactions.types.admin_add')}</option>
              <option value="admin_deduct">{t('admin.transactions.types.admin_deduct')}</option>
              <option value="cashback_claim">{t('admin.transactions.types.cashback_claim')}</option>
              <option value="cashback_earn">{t('admin.transactions.types.cashback_earn')}</option>
              <option value="admin_add_referral">{t('admin.transactions.types.admin_add_referral')}</option>
              <option value="admin_deduct_referral">{t('admin.transactions.types.admin_deduct_referral')}</option>
              <option value="admin_add_cashback">{t('admin.transactions.types.admin_add_cashback')}</option>
              <option value="admin_deduct_cashback">{t('admin.transactions.types.admin_deduct_cashback')}</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.transactions.filters.all')}</option>
              <option value="pending">{t('admin.transactions.status.pending')}</option>
              <option value="completed">{t('admin.transactions.status.completed')}</option>
              <option value="failed">{t('admin.transactions.status.failed')}</option>
              <option value="reversed">{t('admin.transactions.status.reversed')}</option>
            </select>
          </div>

          {/* Payment Method Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.paymentMethod')}
            </label>
            <select
              value={paymentMethodFilter}
              onChange={(e) => {
                setPaymentMethodFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.transactions.filters.all')}</option>
              <option value="Crypto">Crypto</option>
              <option value="Binance">Binance</option>
              <option value="Syriatel Cash">Syriatel Cash</option>
              <option value="Sham Cash">Sham Cash</option>
              <option value="warehouse">Warehouse</option>
            </select>
          </div>

          {/* User ID Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.userId')}
            </label>
            <input
              type="text"
              value={userIdFilter}
              onChange={(e) => {
                setUserIdFilter(e.target.value);
                setPage(1);
              }}
              placeholder={t('admin.transactions.filters.userIdPlaceholder')}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Transaction ID Search */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.search')}
            </label>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              placeholder={t('admin.transactions.filters.searchPlaceholder')}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Currency Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.currency')}
            </label>
            <input
              type="text"
              value={currencyFilter}
              onChange={(e) => {
                setCurrencyFilter(e.target.value);
                setPage(1);
              }}
              placeholder="USD, SYP, EUR..."
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Date From Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.dateFrom')}
            </label>
            <input
              type="date"
              value={dateFromFilter}
              onChange={(e) => {
                setDateFromFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Date To Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.dateTo')}
            </label>
            <input
              type="date"
              value={dateToFilter}
              onChange={(e) => {
                setDateToFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Min Amount Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.minAmount')}
            </label>
            <input
              type="number"
              value={minAmountFilter}
              onChange={(e) => {
                setMinAmountFilter(e.target.value);
                setPage(1);
              }}
              placeholder="0"
              step="0.01"
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Max Amount Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.maxAmount')}
            </label>
            <input
              type="number"
              value={maxAmountFilter}
              onChange={(e) => {
                setMaxAmountFilter(e.target.value);
                setPage(1);
              }}
              placeholder="1000"
              step="0.01"
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.sortBy')}
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as typeof sortBy);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="createdAt">{t('admin.transactions.sort.createdAt')}</option>
              <option value="updatedAt">{t('admin.transactions.sort.updatedAt')}</option>
              <option value="amount">{t('admin.transactions.sort.amount')}</option>
              <option value="type">{t('admin.transactions.sort.type')}</option>
              <option value="status">{t('admin.transactions.sort.status')}</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.transactions.filters.sortOrder')}
            </label>
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(parseInt(e.target.value) as -1 | 1);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="-1">{t('admin.transactions.sort.descending')}</option>
              <option value="1">{t('admin.transactions.sort.ascending')}</option>
            </select>
          </div>
        </div>

        {/* Reset Filters Button */}
        <div className="mt-4">
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 bg-neutral-dark text-neutral-light font-body rounded-lg hover:bg-neutral-dark/80 transition-colors"
          >
            {t('admin.transactions.filters.reset')}
          </button>
        </div>
      </div>

      {/* Transactions List */}
      {transactions.length === 0 ? (
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <p className="text-neutral-medium text-lg font-body">
            {t('admin.transactions.empty')}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table View - يخفى على الموبايل ويُستبدل بقائمة البطاقات */}
          <div className="hidden md:block card-glass rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-dark/50">
                  <tr>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.transactions.table.id')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.transactions.table.userId')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.transactions.table.type')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.transactions.table.amount')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.transactions.table.status')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.transactions.table.paymentMethod')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap min-w-[120px]">
                      {t('admin.transactions.table.notes')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.transactions.table.created')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-dark">
                  {transactions.map((transaction) => (
                    <tr key={transaction._id} onClick={() => setSelectedTx(transaction)} className="hover:bg-neutral-dark/40 transition-all cursor-pointer group">
                      <td className="px-3 sm:px-6 py-4">
                        <Link
                          to={`/admin/users/${transaction.user ?? transaction.userObjectId ?? transaction.userId}`}
                          className="text-xs sm:text-sm text-brand-accent font-body font-mono hover:underline"
                        >
                          {transaction._id.slice(-8)}
                        </Link>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span className="text-xs sm:text-sm text-neutral-light font-body">
                          {transaction.userId}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span className="text-xs sm:text-sm text-neutral-light font-body">
                          {getTransactionTypeLabel(transaction.type, transaction.paymentDetails)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span className={`text-xs sm:text-sm font-heading font-semibold ${getAmountColor(transaction.amount)}`}>
                          {transaction.amount > 0 ? '+' : ''}
                          {transaction.amount.toFixed(2)} {transaction.currency}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-body ${getStatusBadgeColor(transaction.status)}`}
                        >
                          {t(`admin.transactions.status.${transaction.status}`)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <span className="text-xs sm:text-sm text-neutral-medium font-body">
                          {transaction.paymentMethod || '-'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 max-w-[200px]">
                        <span className="text-xs sm:text-sm text-neutral-medium font-body line-clamp-2" title={transaction.notes ?? undefined}>
                          {transaction.notes || '-'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm text-neutral-medium font-body">
                          {formatDate(transaction.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* عرض الموبايل: قائمة بطاقات بدل الجدول */}
          <div className="md:hidden space-y-4 mt-6">
            {transactions.map((transaction) => (
              <div
  key={transaction._id}
  onClick={() => setSelectedTx(transaction)}
  className="card-glass rounded-xl p-4 border border-neutral-dark/50 space-y-3 cursor-pointer active:scale-95 transition-transform"
>
                <div className="flex justify-between items-start gap-2">
                  <span className="text-xs text-neutral-medium font-body">
                    {t('admin.transactions.table.id')}: {transaction._id.slice(-8)}
                  </span>
                  <span className={`text-sm font-heading font-semibold ${getAmountColor(transaction.amount)}`}>
                    {transaction.amount > 0 ? '+' : ''}
                    {transaction.amount.toFixed(2)} {transaction.currency}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <span className="text-neutral-medium">{t('admin.transactions.table.type')}:</span>
                  <span className="text-neutral-light font-body">{getTransactionTypeLabel(transaction.type, transaction.paymentDetails)}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  <span className="text-neutral-medium">{t('admin.transactions.table.status')}:</span>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-body ${getStatusBadgeColor(transaction.status)}`}>
                    {t(`admin.transactions.status.${transaction.status}`)}
                  </span>
                </div>
                {(transaction.paymentMethod || transaction.notes) && (
                  <div className="pt-2 border-t border-neutral-dark/50 space-y-1 text-sm">
                    {transaction.paymentMethod && (
                      <p className="text-neutral-medium font-body">
                        {t('admin.transactions.table.paymentMethod')}: {transaction.paymentMethod}
                      </p>
                    )}
                    {transaction.notes && (
                      <p className="text-neutral-light font-body">
                        <span className="text-neutral-medium">{t('admin.transactions.table.notes')}:</span> {transaction.notes}
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-neutral-medium font-body">
                  {t('admin.transactions.table.created')}: {formatDate(transaction.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                </p>
                <Link
                  to={`/admin/users/${transaction.user ?? transaction.userObjectId ?? transaction.userId}`}
                  className="inline-block text-xs text-brand-accent font-body hover:underline"
                >
                  {t('admin.transactions.table.userId')}: {transaction.userId}
                </Link>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('catalog.pagination.previous')}
              </button>

              <div className="px-4 py-2 bg-neutral-dark/50 text-neutral-light font-body rounded-lg">
                {t('catalog.pagination.page')} {pagination.page} {t('catalog.pagination.of')} {pagination.pages}
              </div>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('catalog.pagination.next')}
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {pagination && (
            <div className="text-center text-neutral-medium font-body mt-4">
              {t('catalog.pagination.showing')} {transactions.length} {t('catalog.pagination.of')} {pagination.total} {t('admin.transactions.pagination.transactions')}
            </div>
          )}
        </>
      )}
    {/* النافذة المنبثقة لتفاصيل المعاملة (Admin Modal) */}
      {selectedTx && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in text-right" dir="rtl">
          <div className="card-glass border border-brand-accent/50 rounded-3xl max-w-2xl w-full p-6 md:p-10 shadow-2xl overflow-y-auto max-h-[90vh] relative animate-scale-in">
            
            {/* زر إغلاق */}
            <button 
              onClick={() => setSelectedTx(null)} 
              className="absolute top-6 left-6 text-neutral-medium hover:text-brand-accent transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-8">
              <h3 className="text-3xl font-heading font-bold text-brand-accent mb-2">تفاصيل المعاملة</h3>
              <p className="text-neutral-medium text-xs font-mono bg-white/5 inline-block px-4 py-1 rounded-full">ID: {selectedTx._id}</p>
            </div>
            
            <div className="space-y-6 font-body">
              
              {/* خلاصة سريعة: ماذا اشترى؟ */}
<div className="bg-brand-accent/10 border border-brand-accent/30 p-5 rounded-2xl text-center">
  <p className="text-brand-accent text-xs uppercase font-bold mb-2">الخدمة / المنتج</p>
  <p className="text-xl text-white font-heading font-bold">
    {selectedTx.relatedEntityDetails?.serviceName ||
     selectedTx.relatedEntityDetails?.productName ||
     selectedTx.paymentDetails?.service ||
     selectedTx.paymentDetails?.serviceName ||
     selectedTx.paymentDetails?.item ||
     selectedTx.relatedEntityDetails?.number ||
     selectedTx.paymentDetails?.number ||
     selectedTx.paymentDetails?.productName ||
     (selectedTx.relatedEntityDetails?.notes || selectedTx.notes ? selectedTx.relatedEntityDetails?.notes || selectedTx.notes : null) ||
     (selectedTx.type === "recharge" ? "شحن رصيد" :
      selectedTx.type === "refund" ? "استرداد تلقائي" :
      selectedTx.type === "referral_bonus" ? "مكافأة إحالة" :
      selectedTx.type === "purchase" ? "شراء خدمة" :
      "معاملة عامة")}
  </p>
</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* بيانات العميل */}
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <p className="text-neutral-dark text-[10px] uppercase mb-2">معرف المستخدم</p>
                  <p className="text-white font-mono text-sm mb-2">{selectedTx.userId}</p>
                  <Link 
                    to={`/admin/users/${selectedTx.user ?? selectedTx.userObjectId ?? selectedTx.userId}`}
                    className="text-brand-accent hover:underline text-xs flex items-center gap-1 justify-center"
                  >
                    <span>🔍 عرض ملف المستخدم</span>
                  </Link>
                </div>

                {/* الحساب المالي */}
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <p className="text-neutral-dark text-[10px] uppercase mb-2">العملية المالية</p>
                  <p className={`text-lg font-bold ${selectedTx.amount > 0 ? 'text-brand-accent' : 'text-danger'}`}>
                    {selectedTx.amount.toFixed(2)} {selectedTx.currency}
                  </p>
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-bold ${selectedTx.status === "completed" ? "bg-brand-accent/20 text-brand-accent" : selectedTx.status === "failed" ? "bg-danger/20 text-danger" : "bg-white/10 text-white"}`}>{selectedTx.status}</span>
                </div>
              </div>

              {/* المصدر (Provider Badge) */}
              {selectedTx.paymentDetails?.provider && !selectedTx.relatedEntityDetails?.provider && (
                <div className="flex justify-center">
                  <span className="text-[11px] bg-brand-accent/15 text-brand-accent px-3 py-1 rounded-full font-bold">
                    {selectedTx.paymentDetails.provider}
                  </span>
                </div>
              )}

              {/* بيانات الرقم (Number Details) */}
              {selectedTx.relatedEntityType === 'number' && selectedTx.relatedEntityDetails && (
                <div className="bg-black/40 p-6 rounded-2xl border border-brand-accent/30">
                  <p className="text-brand-accent font-bold text-sm mb-4 border-b border-white/10 pb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    بيانات الرقم
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">النوع:</span>
                      <span className={`text-xs font-bold ${selectedTx.relatedEntityDetails.numberType === 'permanent' ? 'text-green-500' : 'text-blue-400'}`}>
                        {selectedTx.relatedEntityDetails.numberType === 'permanent' ? 'رقم دائم' : 'رقم مؤقت'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">الرقم:</span>
                      <span className="text-white font-mono text-xs text-left">{selectedTx.relatedEntityDetails.number || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">الخدمة:</span>
                      <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.serviceName || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">الدولة:</span>
                      <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.countryName || '-'}</span>
                    </div>
                    {selectedTx.relatedEntityDetails.stateName && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">الولاية:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.stateName}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">المزود:</span>
                      <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.provider || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">المصدر:</span>
                      <span className="text-white font-mono text-[10px] text-left">{selectedTx.relatedEntityDetails.sourceCollection || '-'}</span>
                    </div>
                    {selectedTx.relatedEntityDetails.price && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">السعر:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.price}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">الحالة:</span>
                      <span className={`text-xs font-bold text-left ${selectedTx.relatedEntityDetails.status === 'active' ? 'text-green-500' : selectedTx.relatedEntityDetails.status === 'expired' ? 'text-red-500' : 'text-yellow-400'}`}>
                        {selectedTx.relatedEntityDetails.status || '-'}
                      </span>
                    </div>
                    {selectedTx.relatedEntityDetails.externalServiceId && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5 md:col-span-2">
                        <span className="text-white/60">معرّف الطلب:</span>
                        <span className="text-white font-mono text-[10px] text-left">{selectedTx.relatedEntityDetails.externalServiceId}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* بيانات المنتج (Product Details) */}
              {selectedTx.relatedEntityType === 'product' && selectedTx.relatedEntityDetails && (
                <div className="bg-black/40 p-6 rounded-2xl border border-brand-accent/30">
                  <p className="text-brand-accent font-bold text-sm mb-4 border-b border-white/10 pb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                    بيانات المنتج
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">اسم المنتج:</span>
                      <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.productName || '-'}</span>
                    </div>
                    {selectedTx.relatedEntityDetails.productValue && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">القيمة:</span>
                        <span className="text-white font-mono text-xs text-left break-all">{selectedTx.relatedEntityDetails.productValue}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-1 border-b border-white/5">
                      <span className="text-white/60">التصنيف:</span>
                      <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.categoryName || selectedTx.relatedEntityDetails.categoryNameEn || '-'}</span>
                    </div>
                    {selectedTx.relatedEntityDetails.description && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5 md:col-span-2">
                        <span className="text-white/60">الوصف:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* بيانات البروكسي (Proxy Details) */}
              {selectedTx.paymentDetails?.proxyInfo && Array.isArray(selectedTx.paymentDetails.proxyInfo) && selectedTx.paymentDetails.proxyInfo.length > 0 && (
                <div className="bg-black/40 p-6 rounded-2xl border border-brand-accent/30">
                  <p className="text-brand-accent font-bold text-sm mb-4 border-b border-white/10 pb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    بيانات البروكسي
                  </p>
                  <div className="space-y-4">
                    {selectedTx.paymentDetails.proxyInfo.map((proxy: any, pIdx: number) => (
                      <div key={pIdx} className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="text-[10px] text-neutral-dark mb-2">بروكسي #{pIdx + 1}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div className="flex justify-between items-center py-1 border-b border-white/5">
                            <span className="text-white/60">IP:</span>
                            <span className="text-white font-mono text-xs break-all text-left">{proxy.ipAddress || '-'}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-white/5">
                            <span className="text-white/60">Port:</span>
                            <span className="text-white font-mono text-xs text-left">{proxy.port || '-'}</span>
                          </div>
                          {proxy.port_socks && (
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-white/60">Port SOCKS5:</span>
                              <span className="text-white font-mono text-xs text-left">{proxy.port_socks}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-1 border-b border-white/5">
                            <span className="text-white/60">Username:</span>
                            <span className="text-white font-mono text-xs text-left dir-ltr" dir="ltr">{proxy.username || '-'}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-white/5">
                            <span className="text-white/60">Password:</span>
                            <span className="text-white font-mono text-xs text-left dir-ltr" dir="ltr">{proxy.password || '-'}</span>
                          </div>
                          <div className="flex justify-between items-center py-1 border-b border-white/5">
                            <span className="text-white/60">الموقع:</span>
                            <span className="text-white text-xs text-left">{proxy.location || proxy.country || '-'}</span>
                          </div>
                          {proxy.city && (
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-white/60">المدينة:</span>
                              <span className="text-white text-xs text-left">{proxy.city}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-1 border-b border-white/5">
                            <span className="text-white/60">النوع:</span>
                            <span className="text-white text-xs text-left">{proxy.type || '-'}</span>
                          </div>
                          {proxy.carrier && (
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-white/60">Carrier:</span>
                              <span className="text-white text-xs text-left">{proxy.carrier}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-1 border-b border-white/5">
                            <span className="text-white/60">معرّف خارجي:</span>
                            <span className="text-white font-mono text-[10px] break-all text-left">{proxy.externalServiceId || '-'}</span>
                          </div>
                          {proxy.expiresAt && (
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-white/60">انتهاء:</span>
                              <span className="text-white text-xs text-left">{formatDate(proxy.expiresAt, 'PP', currentLanguage, 'Asia/Damascus')}</span>
                            </div>
                          )}
                          {proxy.status && (
                            <div className="flex justify-between items-center py-1 border-b border-white/5">
                              <span className="text-white/60">الحالة:</span>
                              <span className={`text-xs font-bold text-left ${proxy.status === 'active' ? 'text-green-500' : 'text-red-500'}`}>{proxy.status}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* تفاصيل الشراء الأساسية (للمعاملات القديمة بدون بيانات مثرية) */}
              {selectedTx.relatedEntityType === 'purchase' && selectedTx.relatedEntityDetails?.isBasicInfo && (
                <div className="bg-black/40 p-6 rounded-2xl border border-brand-accent/30">
                  <p className="text-brand-accent font-bold text-sm mb-4 border-b border-white/10 pb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                    تفاصيل الشراء
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    {selectedTx.relatedEntityDetails.serviceName && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">الخدمة:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.serviceName}</span>
                      </div>
                    )}
                    {selectedTx.relatedEntityDetails.number && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">الرقم:</span>
                        <span className="text-white font-mono text-xs text-left">{selectedTx.relatedEntityDetails.number}</span>
                      </div>
                    )}
                    {selectedTx.relatedEntityDetails.country && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">الدولة:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.country}</span>
                      </div>
                    )}
                    {selectedTx.relatedEntityDetails.productName && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">المنتج:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.productName}</span>
                      </div>
                    )}
                    {selectedTx.relatedEntityDetails.categoryName && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">الفئة:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.categoryName}</span>
                      </div>
                    )}
                    {selectedTx.relatedEntityDetails.notes && (
                      <div className="flex justify-between items-center py-1 border-b border-white/5">
                        <span className="text-white/60">ملاحظات:</span>
                        <span className="text-white text-xs text-left">{selectedTx.relatedEntityDetails.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* المعلومات التقنية (Metadata) */}
<div className="bg-black/40 p-6 rounded-2xl border border-white/5">
  <p className="text-brand-accent font-bold text-sm mb-4 border-b border-white/10 pb-2">
    تفاصيل المعاملة:
  </p>

  <div className="space-y-3">
    <div className="flex justify-between text-sm py-1 border-b border-white/5 gap-4">
      <span className="text-white shrink-0">الملاحظات:</span>
      <span className="text-white text-left italic break-words">
        {selectedTx.notes || "-"}
      </span>
    </div>

    {selectedTx.paymentDetails &&
    typeof selectedTx.paymentDetails === "object" &&
    Object.keys(selectedTx.paymentDetails).length > 0 ? (
      Object.entries(selectedTx.paymentDetails)
        .filter(([key]) => key !== "proxyInfo") // معروض في قسم البروكسي أعلاه
        .map(([key, val]) => (
        <div
          key={key}
          className="flex justify-between items-start text-sm py-1 border-b border-white/5 gap-4"
        >
          <span className="text-white capitalize shrink-0">
            {key.replace(/([A-Z])/g, " $1")}:
          </span>
          <span className="text-white/80 font-mono text-xs break-all text-left">
            {typeof val === "object" ? JSON.stringify(val) : String(val)}
          </span>
        </div>
      ))
    ) : (
      <div className="space-y-2">
        <div className="text-center py-2 text-white/60 text-xs italic">
          لا توجد بيانات تقنية إضافية مخزنة
        </div>

        <div className="flex justify-between items-start text-sm py-1 border-b border-white/5 gap-4">
          <span className="text-white shrink-0">نوع المعاملة:</span>
          <span className={`text-xs font-bold ${selectedTx.type === "recharge" ? "text-brand-accent" : selectedTx.type === "refund" ? "text-yellow-400" : selectedTx.type === "purchase" ? "text-danger" : "text-white"}`}>{selectedTx.type || "-"}</span>
        </div>

        {selectedTx.paymentMethod && (
          <div className="flex justify-between items-start text-sm py-1 border-b border-white/5 gap-4">
            <span className="text-white shrink-0">طريقة الدفع:</span>
            <span className="text-xs font-mono bg-brand-accent/10 text-brand-accent px-2 py-0.5 rounded">
              {selectedTx.paymentMethod}
            </span>
          </div>
        )}

        {selectedTx.productId && (
          <div className="flex justify-between items-start text-sm py-1 border-b border-white/5 gap-4">
            <span className="text-white shrink-0">معرّف المنتج:</span>
            <span className="text-white text-xs break-all text-left">
              {selectedTx.productId}
            </span>
          </div>
        )}

        {selectedTx.categoryId && (
          <div className="flex justify-between items-start text-sm py-1 border-b border-white/5 gap-4">
            <span className="text-white shrink-0">معرّف التصنيف:</span>
            <span className="text-white text-xs break-all text-left">
              {selectedTx.categoryId}
            </span>
          </div>
        )}
      </div>
    )}
  </div>
</div>
</div>

<div className="mt-6 text-center text-[10px] text-neutral-medium border-t border-white/5 pt-4">
  تاريخ التسجيل: {formatDate(selectedTx.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
</div>

<button
  onClick={() => setSelectedTx(null)}
  className="w-full mt-6 py-4 bg-brand-accent text-brand-primary font-bold rounded-2xl hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-brand-accent/20"
>
  إغلاق النافذة
</button>
</div>
</div>
)}
</div>
);
}